import { createBrowserRouter, Navigate, Outlet, ScrollRestoration } from "react-router-dom"
import { LandingPage } from "@/components/LandingPage"
import { LoginPage } from "@/components/LoginPage"
import { BookDemoPage } from "@/components/BookDemoPage"
import { InviteAcceptPage } from "@/components/InviteAcceptPage"
import { ForgotPasswordPage } from "@/components/ForgotPasswordPage"
import { ResetPasswordPage } from "@/components/ResetPasswordPage"
import { LegalPage } from "@/components/LegalPage"
import { ProtectedRoute } from "@/components/ProtectedRoute"
import { BidsPage } from "@/components/BidsPage"
import { BidScoped } from "@/components/BidScoped"
import { BidDetailsPage } from "@/components/BidDetailsPage"
import { LegacyBidRedirect } from "@/components/bid-menu/LegacyBidRedirect"
import { ArtifactDetailPage } from "@/components/ArtifactDetailPage"
import { DocumentProposalPage } from "@/components/structure-proposal/DocumentProposalPage"
import { SectionEditorPage } from "@/components/SectionEditorPage"
import { ResponseDocumentRoute } from "@/components/ResponseDocumentRoute"
import { QuestionnaireOverviewPage } from "@/components/QuestionnaireOverviewPage"
import { QuestionEditorPage } from "@/components/QuestionEditorPage"
import { ExcelSchedulePage } from "@/components/schedule/ExcelSchedulePage"
import { WordFormPage } from "@/components/word-form/WordFormPage"
import { AssistantPage } from "@/components/AssistantPage"
import { SettingsPage } from "@/components/SettingsPage"
import { KnowledgePage } from "@/components/knowledge/KnowledgePage"
import { LegacyKnowledgeRedirect } from "@/components/knowledge/LegacyKnowledgeRedirect"
import { HelpPage } from "@/components/help/HelpPage"
import { HomePage } from "@/components/HomePage"
import { MyWorkPage } from "@/components/my-work/MyWorkPage"
import { AppLayout } from "@/components/layouts/AppLayout"
import { RouteErrorFallback } from "@/components/RouteErrorFallback"
import { PlatformAdminRoute } from "@/components/PlatformAdminRoute"
import { AdminDashboardPage } from "@/components/admin/AdminDashboardPage"
import { AdminDemoRequestsPage } from "@/components/admin/AdminDemoRequestsPage"
import { AdminOrganizationsPage } from "@/components/admin/AdminOrganizationsPage"
import { AdminUsagePage } from "@/components/admin/AdminUsagePage"
import { AdminUsersPage } from "@/components/admin/AdminUsersPage"
import { MfaChallengePage } from "@/components/MfaChallengePage"
import { MfaEnrollPage } from "@/components/MfaEnrollPage"
import { CookieConsentBanner } from "@/components/CookieConsentBanner"

export function bidsLoader() {
  // TODO: Fetch bids from API
  return { bids: [] }
}

export function bidDetailsLoader({ params }: { params: { bidId?: string } }) {
  // TODO: Fetch single bid from API
  const { bidId } = params
  if (!bidId) throw new Response("Bid ID required", { status: 400 })
  return { bidId, bid: null }
}

/**
 * Bid sections (R4 bid menu) for `/app/bids/:bidId/:section`, plus the
 * pre-R4 names, which are redirect-only. Defined in `@/lib/bid-sections`.
 */
export type { BidSection, LegacyBidSection } from "@/lib/bid-sections"
export { BID_SECTIONS, LEGACY_BID_SECTIONS } from "@/lib/bid-sections"

// Root layout — wraps every route with ScrollRestoration so cross-page navigation
// starts at the top (browser preserves prior scroll position by default).
// The cookie banner lives here too: inside the router it re-checks the path on
// every navigation, so entering /app hides it without a page reload.
function RootLayout() {
  return (
    <>
      <ScrollRestoration />
      <Outlet />
      <CookieConsentBanner />
    </>
  )
}

// Route configuration using createBrowserRouter
export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
  // Public routes
  {
    path: "/",
    element: window.location.hostname === 'app.bidcontext.ai'
      ? <Navigate to="/login" replace />
      : <LandingPage />,
  },
  {
    path: "/landing",
    element: <LandingPage />,
  },
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/book-a-demo",
    element: <BookDemoPage />,
  },
  {
    path: "/signup",
    element: <Navigate to="/book-a-demo" replace />,
  },
  {
    path: "/invite/:token",
    element: <InviteAcceptPage />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPasswordPage />,
  },
  {
    path: "/reset-password",
    element: <ResetPasswordPage />,
  },
  {
    path: "/mfa-challenge",
    element: <MfaChallengePage />,
  },
  {
    path: "/mfa-enroll",
    element: <MfaEnrollPage />,
  },
  {
    path: "/privacy",
    element: <LegalPage />,
  },
  {
    path: "/terms",
    element: <LegalPage />,
  },
  {
    path: "/cookies",
    element: <LegalPage />,
  },
  {
    path: "/security",
    element: <LegalPage />,
  },

  // Authenticated app routes with shared layout
  {
    path: "/app",
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorFallback />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: "my-work",
        element: <MyWorkPage />,
      },
      {
        path: "bids",
        element: <BidsPage />,
        loader: bidsLoader,
      },
      // Bid workspace (R4). BidDetailsPage renders the section named by the
      // path: /app/bids/:bidId → Overview; qualify, plan and submit carry a
      // ?view= sub-nav. See `@/lib/bid-sections`.
      {
        path: "bids/:bidId",
        element: <BidScoped><BidDetailsPage /></BidScoped>,
        loader: bidDetailsLoader,
        children: [
          { index: true, element: null },
          { path: "qualify", element: null },
          { path: "plan", element: null },
          { path: "write", element: null },
          { path: "review", element: null },
          { path: "submit", element: null },
          { path: "files", element: null },
          { path: "team", element: null },
          { path: "activity", element: null },
        ],
      },
      // Pre-R4 bid paths: permanent redirects. Notification emails, insight
      // cards and bookmarks still use them. Query params and hash carry over.
      { path: "bids/:bidId/documents", element: <LegacyBidRedirect from="documents" /> },
      { path: "bids/:bidId/analysis", element: <LegacyBidRedirect from="analysis" /> },
      { path: "bids/:bidId/strategy", element: <LegacyBidRedirect from="strategy" /> },
      { path: "bids/:bidId/go-no-go", element: <LegacyBidRedirect from="go-no-go" /> },
      { path: "bids/:bidId/responses", element: <LegacyBidRedirect from="responses" /> },
      { path: "bids/:bidId/submission-pack", element: <LegacyBidRedirect from="submission-pack" /> },
      { path: "bids/:bidId/chat", element: <LegacyBidRedirect from="chat" /> },
      // Response artifact routes
      // /app/bids/:bidId/responses/proposal → AI document proposal review (Layer 1)
      // /app/bids/:bidId/responses/:artifactId → Artifact Detail (Outline Table)
      // /app/bids/:bidId/responses/:artifactId/sections/:sectionKey → Section Drafting
      {
        path: "bids/:bidId/responses/proposal",
        element: <BidScoped><DocumentProposalPage /></BidScoped>,
      },
      {
        path: "bids/:bidId/responses/:artifactId",
        element: <BidScoped><ArtifactDetailPage /></BidScoped>,
      },
      {
        path: "bids/:bidId/responses/:artifactId/questionnaire",
        element: <BidScoped><QuestionnaireOverviewPage /></BidScoped>,
      },
      {
        path: "bids/:bidId/responses/:artifactId/questionnaire/:sectionKey",
        element: <BidScoped><QuestionEditorPage /></BidScoped>,
      },
      {
        path: "bids/:bidId/responses/:artifactId/schedule",
        element: <BidScoped><ExcelSchedulePage /></BidScoped>,
      },
      {
        path: "bids/:bidId/responses/:artifactId/form",
        element: <BidScoped><WordFormPage /></BidScoped>,
      },
      {
        path: "bids/:bidId/responses/:artifactId/document",
        element: <BidScoped><ResponseDocumentRoute /></BidScoped>,
      },
      {
        path: "bids/:bidId/responses/:artifactId/sections/:sectionKey",
        element: <BidScoped><SectionEditorPage /></BidScoped>,
      },
      // Knowledge hub (R4): /app/knowledge/<tab>. Bare /app/knowledge and an
      // unknown tab land on the first tab (Library).
      {
        path: "knowledge",
        element: <KnowledgePage />,
      },
      {
        path: "knowledge/:tab",
        element: <KnowledgePage />,
      },
      // Pre-R4 top-level pages, now Knowledge tabs. Query string and hash carry over.
      {
        path: "library",
        element: <LegacyKnowledgeRedirect tab="library" />,
      },
      {
        path: "templates",
        element: <LegacyKnowledgeRedirect tab="templates" />,
      },
      {
        path: "prompts",
        element: <LegacyKnowledgeRedirect tab="prompts" />,
      },
      {
        path: "assistant/:threadId?",
        element: <AssistantPage />,
      },
      {
        path: "settings",
        element: <SettingsPage />,
      },
      {
        path: "settings/users",
        element: <SettingsPage />,
      },
      {
        path: "settings/export-templates",
        element: <SettingsPage />,
      },
      {
        path: "settings/integrations",
        element: <SettingsPage />,
      },
      {
        path: "help",
        element: <HelpPage />,
      },
      {
        path: "help/:slug",
        element: <HelpPage />,
      },
      // Admin panel (platform admins only)
      {
        path: "admin",
        element: (
          <PlatformAdminRoute>
            <Outlet />
          </PlatformAdminRoute>
        ),
        children: [
          {
            index: true,
            element: <AdminDashboardPage />,
          },
          {
            path: "demo-requests",
            element: <AdminDemoRequestsPage />,
          },
          {
            path: "organizations",
            element: <AdminOrganizationsPage />,
          },
          {
            path: "users",
            element: <AdminUsersPage />,
          },
          {
            path: "usage",
            element: <AdminUsagePage />,
          },
        ],
      },
      // Catch-all redirect for unknown app routes
      {
        path: "*",
        element: <Navigate to="/app" replace />,
      },
    ],
  },

  // Global catch-all redirect
  {
    path: "*",
    element: <Navigate to="/" replace />,
  },
    ],
  },
])
